import { WISHLIST_KEY, RECENT_KEY, clampLimit, readList, toggleWishlist, recordRecent } from './saved-products-store.js';
import { mountProductCards, unmountProductCards } from './product-card.js';

const wishlistEnabled = document.body.dataset.wishlistEnabled === 'true';
const recentEnabled = document.body.dataset.recentEnabled === 'true';
const wishlistMax = clampLimit(document.body.dataset.wishlistMax, 50, 100);
const recentMax = clampLimit(document.body.dataset.recentMax, 12, 20);
const controllers = new WeakMap();

function storage() { try { return window.localStorage; } catch { return { getItem: () => null, setItem: () => { throw new Error('Storage unavailable'); } }; } }
function wishlist() { return readList(storage(), WISHLIST_KEY, wishlistMax); }
function syncWishlist(scope = document) {
  const handles = new Set(wishlist().map(item => item.handle));
  for (const button of scope.querySelectorAll('[data-wishlist-toggle]')) {
    const saved = handles.has(button.dataset.productHandle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? button.dataset.removeLabel : button.dataset.addLabel);
    // Icon buttons (store styles) keep their SVG — CSS fills it from aria-pressed; a label <span> follows the state.
    const label = button.querySelector('span');
    if (button.querySelector('svg')) { if (label) label.textContent = saved ? button.dataset.removeLabel : button.dataset.addLabel; }
    else button.textContent = saved ? '♥' : '♡';
  }
  for (const count of document.querySelectorAll('[data-wishlist-count]')) {
    count.textContent = String(handles.size);
    count.toggleAttribute('data-empty', handles.size === 0);
  }
}

class SavedSurface {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-saved-grid]');
    this.more = root.querySelector('[data-saved-more]');
    this.status = root.querySelector('[data-saved-status]');
    this.empty = root.querySelector('[data-saved-empty]');
    this.abort = new AbortController();
    this.handles = [];
    this.offset = 0;
    this.busy = false;
    this.more?.addEventListener('click', () => this.loadBatch(), { signal: this.abort.signal });
    this.refresh();
  }
  source() {
    const key = this.root.dataset.savedProducts === 'wishlist' ? WISHLIST_KEY : RECENT_KEY;
    const maximum = key === WISHLIST_KEY ? wishlistMax : recentMax;
    return readList(storage(), key, maximum).slice(0, clampLimit(this.root.dataset.max, 8, maximum)).map(item => item.handle);
  }
  refresh() {
    this.abortFetch?.abort();
    this.busy = false;
    unmountProductCards(this.grid);
    this.grid.replaceChildren();
    this.handles = this.source();
    this.offset = 0;
    this.more.hidden = true;
    this.status.textContent = '';
    this.root.hidden = this.root.dataset.savedProducts === 'recent' && (this.handles.length < 2 || this.grid.children.length < 2);
    this.empty.hidden = this.handles.length > 0 || (this.root.dataset.savedProducts === 'recent' && this.root.dataset.showEmpty !== 'true');
    if (this.handles.length) this.loadBatch();
  }
  async loadBatch() {
    if (this.busy || this.offset >= this.handles.length) return;
    this.busy = true;
    this.more.hidden = true;
    const batch = this.handles.slice(this.offset, this.offset + 8);
    this.offset += batch.length;
    this.abortFetch = new AbortController();
    const signal = this.abortFetch.signal;
    let next = 0;
    const results = new Array(batch.length);
    const worker = async () => {
      while (next < batch.length && !signal.aborted) {
        const index = next++;
        try {
          const base = new URL(`${this.root.dataset.root.replace(/\/?$/, '/')}`, location.origin);
          const target = new URL(`products/${encodeURIComponent(batch[index])}`, base);
          target.searchParams.set('section_id', 'saved-product-card');
          const preview = /^(?:home|style)-([a-z]+)$/.exec((location.href ? new URL(location.href).searchParams.get('view') : '') || globalThis.document?.documentElement?.dataset?.previewSuffix || '');
          if (preview) target.searchParams.set('view', `style-${preview[1]}`);
          const response = await fetch(target, { headers: { Accept: 'text/html' }, signal });
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = html.querySelector('[data-product-card]');
          if (card?.dataset.productHandle === batch[index]) results[index] = card;
        } catch { /* An unavailable product is skipped. */ }
      }
    };
    await Promise.all([worker(), worker()]);
    if (signal.aborted) return;
    for (const card of results) if (card) this.grid.append(card);
    this.grid.dataset.count = String(this.grid.children.length);
    if (this.root.dataset.savedProducts === 'recent') this.root.hidden = this.grid.children.length < 2;
    mountProductCards(this.grid);
    syncWishlist(this.grid);
    this.busy = false;
    this.more.hidden = this.offset >= this.handles.length;
    if (this.root.dataset.savedProducts === 'recent' && this.grid.children.length < 2 && this.offset < this.handles.length) {
      void this.loadBatch();
      return;
    }
    if (!this.grid.children.length && this.offset >= this.handles.length) this.status.textContent = this.root.dataset.error;
  }
  destroy() { this.abort.abort(); this.abortFetch?.abort(); unmountProductCards(this.grid); controllers.delete(this.root); }
}
function mount(scope = document) {
  for (const root of [...(scope.matches?.('[data-saved-products]') ? [scope] : []), ...scope.querySelectorAll('[data-saved-products]')]) {
    if (!controllers.has(root)) controllers.set(root, new SavedSurface(root));
  }
  syncWishlist(scope);
}
if (wishlistEnabled) {
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    toggleWishlist(storage(), button.dataset.productHandle, wishlistMax);
    syncWishlist();
    for (const root of document.querySelectorAll('[data-saved-products="wishlist"]')) controllers.get(root)?.refresh();
  });
}
if (recentEnabled) {
  const product = document.querySelector('[data-product-page][data-product-context="pdp"][data-product-id][data-product-handle]');
  if (product?.dataset.productId && product.dataset.productHandle) recordRecent(storage(), product.dataset.productHandle, recentMax);
}
mount();
document.addEventListener('nawiya:cards:mounted', event => syncWishlist(event.detail?.scope || document));
document.addEventListener('shopify:section:load', event => mount(event.target));
document.addEventListener('shopify:section:select', event => mount(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const root of [...(event.target.matches?.('[data-saved-products]') ? [event.target] : []), ...event.target.querySelectorAll('[data-saved-products]')]) controllers.get(root)?.destroy();
});
window.addEventListener('storage', event => { if (event.key === WISHLIST_KEY || event.key === RECENT_KEY) { syncWishlist(); for (const root of document.querySelectorAll('[data-saved-products]')) controllers.get(root)?.refresh(); } });
